import styled from 'styled-components';
import { CSSVars } from '@/styles/styles.config';

export const StyledToolHero = styled.section`
  background: url(/assets/hero-bg.webp);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center 0%;
  color: ${CSSVars.white};
  padding: 140px 20px 140px;
  text-align: center;
  position: relative;
  overflow: hidden;

  h1 {
    font-size: 1.875rem;
    font-weight: 300;
    margin-bottom: 20px;
    line-height: 1.15;
    position: relative;
    color: ${CSSVars.white};

    .highlight {
      font-weight: 600;
      background: linear-gradient(90deg, #a5a8ff, #ff979c);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    font-size: ${CSSVars.fontSizeMD};
    color: rgba(255, 255, 255, 0.8);
    max-width: 580px;
    margin: 0 auto;
    line-height: 1.7;
    font-weight: 300;
    position: relative;
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 160px 40px 160px;
    h1 { font-size: ${CSSVars.fontSize2XL}; }
    p  { font-size: ${CSSVars.fontSizeLG}; }
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 180px 40px 180px;
    h1 { font-size: ${CSSVars.fontSize3XL}; }
  }
`;

export const StyledToolBody = styled.div`
  background: ${CSSVars.gray50};
  padding: 60px 20px 80px;

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 80px 40px 100px;
  }
`;

export const StyledCalculator = styled.div`
  max-width: 860px;
  margin: 0 auto;
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 20px;
  padding: 28px 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);

  h2 {
    font-size: ${CSSVars.fontSizeLG};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 28px;
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 48px;
    h2 {
      font-size: ${CSSVars.fontSizeXL};
      margin-bottom: 32px;
    }
  }
`;

export const StyledFormGroup = styled.div`
  margin-bottom: 24px;

  label {
    display: block;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 600;
    color: ${CSSVars.gray700};
    margin-bottom: 8px;
  }

  input, select {
    width: 100%;
    padding: 13px 16px;
    font-size: ${CSSVars.fontSizeMD};
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 10px;
    color: ${CSSVars.black};
    background: ${CSSVars.white};
    transition: border-color 0.2s, box-shadow 0.2s;
    font-family: ${CSSVars.secondaryFont};

    &::placeholder {
      color: ${CSSVars.gray400};
    }

    &:focus {
      border-color: ${CSSVars.blue500};
      box-shadow: 0 0 0 3px rgba(76, 79, 255, 0.1);
      outline: none;
    }
  }

  textarea {
    width: 100%;
    padding: 13px 16px;
    font-size: ${CSSVars.fontSizeMD};
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 10px;
    color: ${CSSVars.black};
    background: ${CSSVars.white};
    transition: border-color 0.2s, box-shadow 0.2s;
    font-family: ${CSSVars.secondaryFont};
    min-height: 80px;
    resize: vertical;

    &::placeholder {
      color: ${CSSVars.gray400};
    }

    &:focus {
      border-color: ${CSSVars.blue500};
      box-shadow: 0 0 0 3px rgba(76, 79, 255, 0.1);
      outline: none;
    }
  }

  .hint {
    font-size: ${CSSVars.fontSizeXS};
    color: ${CSSVars.gray500};
    margin-top: 6px;
    line-height: 1.5;
  }
`;

export const StyledQAList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
`;

export const StyledQAItem = styled.div`
  background: ${CSSVars.gray50};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  padding: 20px;
  position: relative;

  .qa-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    .qa-number {
      font-size: ${CSSVars.fontSizeSM};
      font-weight: 700;
      color: ${CSSVars.blue500};
    }

    .qa-remove {
      padding: 4px 12px;
      background: none;
      border: 1px solid rgba(220, 38, 38, 0.3);
      border-radius: 6px;
      color: #dc2626;
      font-size: ${CSSVars.fontSizeXS};
      font-weight: 600;
      cursor: pointer;
      font-family: ${CSSVars.primaryFont};
      transition: background 0.2s, border-color 0.2s;

      &:hover {
        background: rgba(220, 38, 38, 0.08);
        border-color: rgba(220, 38, 38, 0.5);
      }
    }
  }

  ${StyledFormGroup} {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const StyledAddButton = styled.button`
  width: 100%;
  padding: 13px 24px;
  background: ${CSSVars.white};
  color: ${CSSVars.blue500};
  font-size: ${CSSVars.fontSizeSM};
  font-weight: 700;
  border: 2px dashed rgba(76, 79, 255, 0.3);
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
  font-family: ${CSSVars.primaryFont};
  margin-bottom: 24px;

  &:hover {
    background: rgba(76, 79, 255, 0.04);
    border-color: rgba(76, 79, 255, 0.5);
  }
`;

export const StyledCalculateButton = styled.button`
  width: 100%;
  padding: 15px 24px;
  background: ${CSSVars.blue500};
  color: ${CSSVars.white};
  font-size: ${CSSVars.fontSizeMD};
  font-weight: 700;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.3s, transform 0.1s;
  font-family: ${CSSVars.primaryFont};
  letter-spacing: 0.01em;

  &:hover {
    background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
  }

  &:active {
    transform: scale(0.99);
  }
`;

export const StyledResult = styled.div<{ status: 'poor' | 'fair' | 'good' }>`
  max-width: 860px;
  margin: 32px auto 0;
  scroll-margin-top: 80px;
  background: ${CSSVars.white};
  border: 1px solid ${({ status }) =>
    status === 'good'
      ? 'rgba(74, 222, 128, 0.4)'
      : status === 'fair'
      ? 'rgba(252, 211, 77, 0.4)'
      : 'rgba(248, 113, 113, 0.4)'};
  border-radius: 20px;
  padding: 28px 20px;
  text-align: center;
  box-shadow: 0 4px 32px
    ${({ status }) =>
      status === 'good'
        ? 'rgba(74, 222, 128, 0.12)'
        : status === 'fair'
        ? 'rgba(252, 211, 77, 0.12)'
        : 'rgba(248, 113, 113, 0.12)'};

  .result-label {
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: ${CSSVars.gray500};
    margin-bottom: 12px;
  }

  .result-number {
    font-size: 3rem;
    font-weight: 700;
    line-height: 1;
    margin-bottom: 14px;
    color: ${({ status }) =>
      status === 'good' ? '#16a34a' : status === 'fair' ? '#b45309' : '#dc2626'};

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize4XL};
    }
  }

  .result-badge {
    display: inline-block;
    padding: 5px 16px;
    border-radius: 20px;
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 24px;
    background: ${({ status }) =>
      status === 'good'
        ? 'rgba(22, 163, 74, 0.1)'
        : status === 'fair'
        ? 'rgba(180, 83, 9, 0.1)'
        : 'rgba(220, 38, 38, 0.1)'};
    color: ${({ status }) =>
      status === 'good' ? '#16a34a' : status === 'fair' ? '#b45309' : '#dc2626'};
  }

  .result-benchmark {
    font-size: ${CSSVars.fontSizeMD};
    color: ${CSSVars.gray600};
    margin-bottom: 20px;
    line-height: 1.6;
    strong { color: ${CSSVars.black}; }
  }

  .result-pain {
    font-size: ${CSSVars.fontSizeMD};
    color: ${CSSVars.gray600};
    background: ${CSSVars.gray50};
    border: 1px solid rgba(0, 0, 0, 0.07);
    border-radius: 10px;
    padding: 18px 22px;
    line-height: 1.7;
    text-align: left;
    strong { color: ${CSSVars.black}; }
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 40px 48px;
  }
`;

export const StyledCodeBlock = styled.div`
  margin-top: 24px;
  text-align: left;
  position: relative;

  .code-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    h3 {
      font-size: ${CSSVars.fontSizeMD};
      font-weight: 700;
      color: ${CSSVars.black};
    }
  }

  .copy-button {
    padding: 7px 16px;
    background: ${CSSVars.blue500};
    color: ${CSSVars.white};
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 700;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-family: ${CSSVars.primaryFont};
    transition: background 0.3s;

    &:hover {
      background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
    }
  }

  pre {
    background: linear-gradient(135deg, #09093a 0%, #0d0e20 100%);
    border: 1px solid rgba(76, 79, 255, 0.3);
    border-radius: 12px;
    padding: 24px;
    overflow-x: auto;
    font-size: ${CSSVars.fontSizeXS};
    line-height: 1.7;
    color: ${CSSVars.gray300};
    font-family: monospace;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSizeSM};
    }
  }
`;

export const StyledWidgetsSection = styled.section`
  max-width: 1000px;
  margin: 72px auto 0;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 10px;
    text-align: center;
    line-height: 1.2;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize2XL};
    }
  }

  .subtitle {
    text-align: center;
    color: ${CSSVars.gray600};
    font-size: ${CSSVars.fontSizeMD};
    margin-bottom: 48px;
    font-weight: 300;
  }
`;

export const StyledWidgetGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const StyledCtaBanner = styled.section`
  background: ${CSSVars.white};
  padding: 60px 20px 80px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 80px 30px 100px;
  }

  .banner-inner {
    max-width: 1000px;
    margin: 0 auto;
    background: linear-gradient(135deg, #4c4fff 0%, #6b8cff 100%);
    border-radius: 20px;
    padding: 48px 32px;
    text-align: center;
    color: ${CSSVars.white};

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      padding: 64px 48px;
    }
  }

  h2 {
    font-size: ${CSSVars.fontSizeLG};
    font-weight: 700;
    margin-bottom: 16px;
    line-height: 1.3;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize2XL};
    }
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: rgba(255, 255, 255, 0.85);
    max-width: 520px;
    margin: 0 auto 28px;
    line-height: 1.7;
    font-weight: 300;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSizeMD};
    }
  }

  .banner-cta {
    display: inline-block;
    padding: 14px 32px;
    background: ${CSSVars.white};
    color: #3538cd;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    border-radius: 10px;
    text-decoration: none;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    }
  }
`;

export const StyledRelatedTools = styled.section`
  max-width: 1000px;
  margin: 72px auto 0;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 10px;
    text-align: center;
    line-height: 1.2;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize2XL};
    }
  }

  .subtitle {
    text-align: center;
    color: ${CSSVars.gray600};
    font-size: ${CSSVars.fontSizeMD};
    margin-bottom: 48px;
    font-weight: 300;
  }
`;

export const StyledRelatedToolGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledRelatedToolCard = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 28px 24px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  text-decoration: none;
  color: inherit;
  background: ${CSSVars.white};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover {
    border-color: rgba(76, 79, 255, 0.3);
    box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1);
    transform: translateY(-2px);
  }

  .tool-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(76, 79, 255, 0.08);
    color: ${CSSVars.blue500};

    svg {
      width: 24px;
      height: 24px;
    }
  }

  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
    line-height: 1.3;
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.65;
    flex: 1;
    font-weight: 300;
  }

  .tool-cta {
    padding: 9px 18px;
    background: ${CSSVars.blue500};
    color: ${CSSVars.white};
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    border-radius: 8px;
    transition: background 0.3s;

    &:hover {
      background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
    }
  }
`;

export const StyledWidgetCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 28px 24px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  text-decoration: none;
  color: inherit;
  background: ${CSSVars.white};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover {
    border-color: rgba(76, 79, 255, 0.3);
    box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1);
    transform: translateY(-3px);
  }

  .widget-header {
    display: flex;
    align-items: center;
    gap: 12px;

    img {
      width: 44px;
      height: 44px;
      object-fit: contain;
      border-radius: 10px;
      background: ${CSSVars.gray50};
    }

    h3 {
      font-size: ${CSSVars.fontSizeMD};
      font-weight: 700;
      color: ${CSSVars.black};
      line-height: 1.3;
    }
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.65;
    flex: 1;
    font-weight: 300;
  }

  .widget-cta {
    display: inline-block;
    padding: 10px 18px;
    background: ${CSSVars.blue500};
    color: ${CSSVars.white};
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    border-radius: 8px;
    text-align: center;
    transition: background 0.3s;

    &:hover {
      background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
    }
  }
`;

// ─── Website Schema Generator additions ─────────────────────────────────────────

export const StyledTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0 20px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const StyledFieldsetTitle = styled.h3`
  font-size: ${CSSVars.fontSizeMD};
  font-weight: 700;
  color: ${CSSVars.black};
  margin: 8px 0 20px;
  padding-top: 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);

  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }

  span {
    display: block;
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 400;
    color: ${CSSVars.gray500};
    margin-top: 4px;
    line-height: 1.5;
  }
`;

export const StyledCheckToggle = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  background: ${CSSVars.gray50};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  cursor: pointer;
  margin-bottom: 20px;
  transition: border-color 0.2s, background 0.2s;

  &:hover { border-color: rgba(76, 79, 255, 0.3); }

  input {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    accent-color: ${CSSVars.blue500};
    cursor: pointer;
    flex-shrink: 0;
  }

  .toggle-text {
    b {
      display: block;
      font-size: ${CSSVars.fontSizeSM};
      font-weight: 700;
      color: ${CSSVars.black};
      margin-bottom: 2px;
    }
    span {
      font-size: ${CSSVars.fontSizeXS};
      color: ${CSSVars.gray500};
      line-height: 1.5;
    }
  }
`;

export const StyledRepeatList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
`;

export const StyledRepeatRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  input, select {
    flex: 1;
    min-width: 0;
    padding: 11px 14px;
    font-size: ${CSSVars.fontSizeSM};
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 10px;
    color: ${CSSVars.black};
    background: ${CSSVars.white};
    font-family: ${CSSVars.secondaryFont};

    &:focus {
      border-color: ${CSSVars.blue500};
      box-shadow: 0 0 0 3px rgba(76, 79, 255, 0.1);
      outline: none;
    }
  }

  .remove-row {
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border: 1px solid rgba(220, 38, 38, 0.3);
    background: ${CSSVars.white};
    color: #dc2626;
    border-radius: 10px;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    transition: background 0.2s;

    &:hover { background: rgba(220, 38, 38, 0.08); }
  }
`;

export const StyledSmallAdd = styled.button`
  align-self: flex-start;
  padding: 8px 16px;
  background: rgba(76, 79, 255, 0.08);
  color: ${CSSVars.blue500};
  font-size: ${CSSVars.fontSizeXS};
  font-weight: 700;
  border: 1px solid rgba(76, 79, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  font-family: ${CSSVars.primaryFont};
  margin-bottom: 24px;
  transition: background 0.2s;

  &:hover { background: rgba(76, 79, 255, 0.15); }
`;

export const StyledOutputCard = styled.div`
  max-width: 860px;
  margin: 32px auto 0;
  background: ${CSSVars.white};
  border: 1px solid rgba(76, 79, 255, 0.25);
  border-radius: 20px;
  padding: 28px 20px;
  box-shadow: 0 4px 32px rgba(76, 79, 255, 0.08);

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 40px 48px;
  }

  .output-intro {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.7;
    margin-bottom: 20px;
    font-weight: 300;
    strong { color: ${CSSVars.black}; font-weight: 600; }
  }
`;

export const StyledHowTo = styled.div`
  margin-top: 24px;
  background: ${CSSVars.gray50};
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 12px;
  padding: 20px 22px;

  h4 {
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 12px;
  }

  ol {
    margin: 0 0 0 18px;
    padding: 0;

    li {
      font-size: ${CSSVars.fontSizeSM};
      color: ${CSSVars.gray600};
      line-height: 1.7;
      margin-bottom: 8px;
      font-weight: 300;

      &:last-child { margin-bottom: 0; }

      a { color: ${CSSVars.blue500}; font-weight: 600; text-decoration: none; }
      a:hover { color: ${CSSVars.pink300}; }
      code {
        font-family: monospace;
        background: rgba(76, 79, 255, 0.1);
        padding: 1px 6px;
        border-radius: 4px;
        font-size: 0.85em;
        color: ${CSSVars.blue500};
      }
    }
  }
`;

// AI Schema Generator: URL form + async results additions

export const StyledUrlForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    flex-direction: row;
  }

  input {
    flex: 1;
    min-width: 0;
    padding: 15px 18px;
    font-size: ${CSSVars.fontSizeMD};
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 10px;
    color: ${CSSVars.black};
    background: ${CSSVars.white};
    font-family: ${CSSVars.secondaryFont};

    &::placeholder {
      color: ${CSSVars.gray400};
    }
    &:focus {
      border-color: ${CSSVars.blue500};
      box-shadow: 0 0 0 3px rgba(76, 79, 255, 0.1);
      outline: none;
    }
  }

  button {
    flex-shrink: 0;
    padding: 15px 28px;
    background: ${CSSVars.blue500};
    color: ${CSSVars.white};
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.3s, transform 0.1s;
    font-family: ${CSSVars.primaryFont};

    &:hover:not(:disabled) {
      background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
    }
    &:active:not(:disabled) {
      transform: scale(0.99);
    }
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

export const StyledFormError = styled.p`
  margin-top: 14px;
  padding: 12px 16px;
  background: rgba(220, 38, 38, 0.06);
  border: 1px solid rgba(220, 38, 38, 0.25);
  border-radius: 10px;
  color: #b91c1c;
  font-size: ${CSSVars.fontSizeSM};
  line-height: 1.6;
`;

export const StyledSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 20px;
  color: ${CSSVars.gray600};
  font-size: ${CSSVars.fontSizeSM};

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${CSSVars.blue500};
    animation: aischema-bounce 1.2s infinite ease-in-out both;
  }
  .dot:nth-child(2) { animation-delay: -0.16s; }
  .dot:nth-child(3) { animation-delay: 0s; }
  .dot:nth-child(1) { animation-delay: -0.32s; }

  @keyframes aischema-bounce {
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
    40% { transform: scale(1); opacity: 1; }
  }
`;

export const StyledTypeChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;

  .chip {
    padding: 5px 14px;
    border-radius: 20px;
    background: rgba(76, 79, 255, 0.08);
    border: 1px solid rgba(76, 79, 255, 0.2);
    color: ${CSSVars.blue500};
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 700;
  }
`;

export const StyledReviewNote = styled.div`
  margin: 20px 0;
  padding: 16px 20px;
  background: #fffbeb;
  border: 1px solid rgba(180, 83, 9, 0.3);
  border-radius: 12px;
  color: #92400e;
  font-size: ${CSSVars.fontSizeSM};
  line-height: 1.7;

  strong { font-weight: 700; }
`;

export const StyledNotesBlock = styled.p`
  font-size: ${CSSVars.fontSizeSM};
  color: ${CSSVars.gray700};
  line-height: 1.7;
  margin-bottom: 20px;
  padding: 16px 20px;
  background: ${CSSVars.gray50};
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 12px;

  b { color: ${CSSVars.black}; }
`;
